import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Router } from '@angular/router';

interface HelpSection {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  action: () => void;
}

@Component({
  selector: 'app-help-center',
  standalone: true,
  imports: [CommonModule, ButtonModule, CardModule, DividerModule, TranslocoPipe],
  template: `
    <div class="help-center-dialog p-6 max-w-4xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-8">
        <div
          class="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center"
        >
          <i class="pi pi-question-circle text-white text-3xl"></i>
        </div>
        <h2 class="text-3xl font-bold text-neutral-800 dark:text-neutral-200 mb-2">
          {{ 'helpCenter.title' | transloco }}
        </h2>
        <p class="text-neutral-600 dark:text-neutral-400">
          {{ 'helpCenter.subtitle' | transloco }}
        </p>
      </div>

      <!-- Help Sections Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        @for (section of helpSections; track section.id) {
          <div
            class="help-section-card cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-lg"
            (click)="section.action()"
            [style.background]="section.bgColor"
          >
            <div class="p-6 text-center">
              <div
                class="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-white text-2xl"
                [style.background]="section.color"
              >
                <i [class]="section.icon"></i>
              </div>
              <h3 class="text-xl font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
                {{ section.title }}
              </h3>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">
                {{ section.description }}
              </p>
            </div>
          </div>
        }
      </div>

      <!-- Quick Actions -->
      <div class="bg-neutral-50 dark:bg-neutral-800/50 rounded-lg p-6 mb-6">
        <h3 class="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-4 text-center">
          {{ 'helpCenter.quickActions.title' | transloco }}
        </h3>
        <div class="flex flex-wrap gap-3 justify-center">
          <button
            class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            (click)="openContactSupport()"
          >
            <i class="pi pi-envelope me-2"></i>
            {{ 'helpCenter.quickActions.contact' | transloco }}
          </button>
          <button
            class="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
            (click)="openFeedback()"
          >
            <i class="pi pi-thumbs-up me-2"></i>
            {{ 'helpCenter.quickActions.feedback' | transloco }}
          </button>
          <button
            class="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
            (click)="openTutorial()"
          >
            <i class="pi pi-play me-2"></i>
            {{ 'helpCenter.quickActions.tutorial' | transloco }}
          </button>
        </div>
      </div>

      <!-- Close Button -->
      <div class="text-center">
        <button
          class="px-8 py-3 bg-neutral-200 dark:bg-neutral-700 hover:bg-neutral-300 dark:hover:bg-neutral-600 text-neutral-800 dark:text-neutral-200 rounded-lg font-medium transition-colors"
          (click)="closeDialog()"
        >
          <i class="pi pi-times me-2"></i>
          {{ 'helpCenter.close' | transloco }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .help-section-card {
        border-radius: 12px;
        border: 2px solid transparent;
        transition: all 0.3s ease;
      }

      .help-section-card:hover {
        border-color: rgba(59, 130, 246, 0.3);
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
      }
    `,
  ],
})
export class HelpCenterComponent implements OnInit {
  private dialogRef = inject(DynamicDialogRef);
  router = inject(Router);
  private translocoService = inject(TranslocoService);

  helpSections: HelpSection[] = [];

  ngOnInit() {
    this.initializeHelpSections();
  }

  private initializeHelpSections() {
    this.helpSections = [
      {
        id: 'changelog',
        title: this.translocoService.translate('helpCenter.sections.changelog.title'),
        description: this.translocoService.translate('helpCenter.sections.changelog.description'),
        icon: 'pi pi-history',
        color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        bgColor:
          'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
        action: () => this.openChangelog(),
      },
      {
        id: 'documents',
        title: this.translocoService.translate('helpCenter.sections.documents.title'),
        description: this.translocoService.translate('helpCenter.sections.documents.description'),
        icon: 'pi pi-book',
        color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        bgColor:
          'linear-gradient(135deg, rgba(240, 147, 251, 0.1) 0%, rgba(245, 87, 108, 0.1) 100%)',
        action: () => this.openDocuments(),
      },
      {
        id: 'faqs',
        title: this.translocoService.translate('helpCenter.sections.faqs.title'),
        description: this.translocoService.translate('helpCenter.sections.faqs.description'),
        icon: 'pi pi-question',
        color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        bgColor: 'linear-gradient(135deg, rgba(79, 172, 254, 0.1) 0%, rgba(0, 242, 254, 0.1) 100%)',
        action: () => this.openFAQs(),
      },
    ];
  }

  openChangelog() {
    // TODO: Implement changelog dialog
  }

  openDocuments() {
    this.dialogRef.close();
    this.router.navigate(['/user_guide']);
  }

  openFAQs() {
    this.dialogRef.close();
    this.router.navigate(['/FAQ']);
  }

  openContactSupport() {
    // TODO: Implement contact support
  }

  openFeedback() {
    // TODO: Implement feedback form
  }

  openTutorial() {
    // TODO: Implement tutorial
  }

  closeDialog() {
    this.dialogRef.close();
  }
}
